import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ACCESSORY_ITEM_TYPES } from '../data/accessories';
import {
  getBackpackCapacity,
  getBatteryMoveTickMs,
  getElectronicWarfareCapabilities,
} from '../data/accessories';
import { loadCityContext } from './city/cityContextCache';

const TYPE_LABELS = {
  0: 'Arma',
  1: 'Kit',
  2: 'Municion',
  3: 'Objeto',
  4: 'Componente',
  5: 'Mision',
  6: 'Recurso',
  7: 'Especial',
  8: 'Motor',
  9: 'Radar',
  10: 'Mochila',
  11: 'Guerra electronica',
  12: 'Bateria'
};

const SLOT_META = [
  { key: 'arm_l', label: 'Brazo Izquierdo' },
  { key: 'arm_r', label: 'Brazo Derecho' },
  { key: 'base_l', label: 'Base Izquierda' },
  { key: 'base_r', label: 'Base Derecha' }
];

function looksLikeWeapon(item) {
  if (!item) return false;
  if (Number(item.item_type) === 0) return true;
  const name = String(item.name || '').toLowerCase();
  return /(arma|pistola|rifle|laser|láser|canon|cañon|escopeta)/.test(name);
}

function getItemImageSrc(item) {
  const filename = String(item?.image || '').trim();
  if (!filename || filename === 'default.png') return '';
  const apiBase = import.meta.env.VITE_API_URL || '';
  return `${apiBase}/uploads/gallery/${filename}`;
}

export default function BotInventory({ embedded = false, onClose = null, onInventoryUpdated = null }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [dropQuantities, setDropQuantities] = useState({});
  const [cityContext, setCityContext] = useState(null);

  async function loadInventory() {
    const res = await api.get('/robots/my-inventory');
    setPayload(res.data);
  }

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      try {
        await loadInventory();
      } catch (err) {
        if (mounted) setError(err.response?.data?.error || 'No se pudo cargar inventario');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    bootstrap();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    loadCityContext({ force: true })
      .then((context) => {
        if (mounted) setCityContext(context);
      })
      .catch(() => {
        if (mounted) setCityContext(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const rows = payload?.items || [];
  const visibleRows = rows.filter((row) => !Object.values(ACCESSORY_ITEM_TYPES).includes(Number(row.item_type)));
  const accessories = payload?.accessories || {};
  const ammoMagazines = Number(payload?.ammoMagazines || 0);
  const ammoShots = Number(payload?.ammoShots || 0);
  const backpackItemCount = Number(payload?.backpackItemCount || 0);
  const backpackWeapons = useMemo(
    () => visibleRows.filter((row) => looksLikeWeapon(row)),
    [visibleRows]
  );
  const equippedRadar = accessories.radar || null;
  const equippedBackpack = accessories.backpack || null;
  const equippedBattery = accessories.battery || null;
  const equippedEW = accessories.ewar || null;
  const backpackCapacity = getBackpackCapacity(equippedBackpack);
  const movementTick = getBatteryMoveTickMs(equippedBattery);
  const ewCaps = getElectronicWarfareCapabilities(equippedEW);
  const inCity = Boolean(cityContext?.city);
  const cityName = cityContext?.city?.name || 'esta ciudad';
  const cityType = Number(cityContext?.city?.city_type ?? -1);
  const cityTypeLabel = cityType === 1 ? 'pueblo' : cityType === 2 ? 'ciudad' : cityType === 3 ? 'capital' : 'asentamiento';
  const isMineItem = (item) => Number(item?.item_type || 0) === 7;

  async function equipItem(inventoryId, slot) {
    setBusy(true);
    setError('');
    try {
      await api.post('/robots/my-inventory/equip', { inventoryId, slot });
      await loadInventory();
      await onInventoryUpdated?.();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo equipar el arma');
    } finally {
      setBusy(false);
    }
  }

  async function unequipSlot(slot) {
    setBusy(true);
    setError('');
    try {
      await api.post('/robots/my-inventory/unequip', { slot });
      await loadInventory();
      await onInventoryUpdated?.();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo retirar el arma');
    } finally {
      setBusy(false);
    }
  }

  async function dropItem(item, mode = 'ground') {
    if (!item) return;

    setBusy(true);
    setError('');
    try {
      const quantity = mode === 'mine'
        ? 1
        : Math.max(1, Math.min(Number(dropQuantities[item.id] || 1), Number(item.quantity || 1)));

      await api.post('/robots/map-object/drop', {
        inventoryId: item.id,
        quantity,
        mode
      });
      await loadInventory();
      await onInventoryUpdated?.();
      setDropQuantities((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo soltar el objeto');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className={`${embedded ? 'min-h-[320px]' : 'min-h-screen'} bg-[#04060d] text-cyan-200 flex items-center justify-center`}>
        Cargando inventario tactico...
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'min-h-0' : 'min-h-screen'} bg-[#04060d] text-slate-100`}>
      <div className={`${embedded ? 'max-w-none px-4 py-4' : 'max-w-7xl mx-auto px-4 py-8'} space-y-5`}>
        <div className="rounded-2xl border border-cyan-500/30 bg-[#08101f] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-cyan-300">Inventario y Armamento del BOT</h1>
              <p className="text-slate-300/80 text-sm">
                BOT: <span className="text-cyan-200 font-bold">{payload?.bot?.name || 'Sin nombre'}</span>
              </p>
            </div>
            {embedded ? (
              <button
                onClick={() => onClose?.()}
                className="rounded-lg border border-slate-600 px-3 py-2 text-slate-200 hover:bg-slate-800/40"
              >
                Cerrar
              </button>
            ) : (
              <button onClick={() => navigate('/dashboard')} className="rounded-lg border border-slate-600 px-3 py-2 text-slate-200 hover:bg-slate-800/40">Volver al panel</button>
            )}
          </div>
          {error && <div className="mt-3 text-rose-300 text-sm">{error}</div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Objetos totales</div>
            <div className="text-cyan-200 font-bold text-2xl">{payload?.summary?.totalItems ?? 0}</div>
          </div>
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Peso total</div>
            <div className="text-cyan-200 font-bold text-2xl">{payload?.summary?.totalWeight ?? 0}</div>
          </div>
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Cargador total</div>
            <div className="text-cyan-200 font-bold text-2xl">{ammoMagazines}</div>
            <div className="text-slate-400 text-xs">Municion total: {ammoShots} disparos</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Radar activo</div>
            <div className="text-cyan-200 font-bold text-lg">{equippedRadar?.name || 'Sin radar'}</div>
          </div>
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Mochila activa</div>
            <div className="text-cyan-200 font-bold text-lg">{equippedBackpack?.name || 'Sin mochila'}</div>
            <div className="text-slate-400 text-xs">Artículos: {backpackItemCount}/{backpackCapacity}</div>
            <div className="text-slate-400 text-xs">Capacidad de mochila: {backpackCapacity} artículos</div>
          </div>
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Bateria activa</div>
            <div className="text-cyan-200 font-bold text-lg">{equippedBattery?.name || 'Sin bateria'}</div>
            <div className="text-slate-400 text-xs">Retardo de paso: {movementTick} ms</div>
          </div>
          <div className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <div className="text-slate-400 text-sm">Guerra electronica</div>
            <div className="text-cyan-200 font-bold text-lg">{equippedEW?.name || 'Sin modulo'}</div>
            <div className="text-slate-400 text-xs">Infiltracion: {ewCaps.canBypassForeignCities ? 'si' : 'no'} | Minas: {ewCaps.canJamMines ? 'si' : 'no'}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4">
          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="font-bold text-cyan-300 mb-3">Armamento Activo</h2>
            <div className="space-y-3">
              {SLOT_META.map((slot) => {
                const equipped = payload?.equipped?.[slot.key];
                return (
                  <div key={slot.key} className="rounded-lg border border-slate-700 p-3 bg-[#060c18]">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
                          {equipped && getItemImageSrc(equipped) ? (
                            <img src={getItemImageSrc(equipped)} alt={equipped.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[10px] text-slate-500">No tiene nada</span>
                          )}
                        </div>
                        <div>
                          <div className="text-slate-400 text-xs">{slot.label}</div>
                          <div className="text-cyan-100 font-semibold">{equipped?.name || 'Sin arma equipada'}</div>
                          {equipped && (
                            <div className="text-xs text-slate-400">
                              Daño: {equipped.power || 0} | Municion disponible: {ammoShots} disparos
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => unequipSlot(slot.key)}
                        disabled={!equipped || busy}
                        className="rounded-md border border-rose-400/50 px-2 py-1 text-xs text-rose-200 disabled:opacity-40"
                      >
                        Retirar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4">
            <h2 className="font-bold text-cyan-300 mb-3">Mochila</h2>
            <div className="mb-3 rounded-lg border border-cyan-500/20 bg-[#060c18] p-3 text-sm text-slate-300">
              Puedes llevar <span className="text-cyan-200 font-bold">{backpackCapacity}</span> artículos en total.
              {' '}Ahora llevas <span className="text-cyan-200 font-bold">{backpackItemCount}</span>.
            </div>
            {inCity && (
              <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-100">
                Este bot esta dentro de {cityTypeLabel} {cityName}. Para soltar objetos usa el almacen.
              </div>
            )}
            {visibleRows.length === 0 && <p className="text-slate-400 text-sm">No hay objetos en inventario todavia.</p>}
            <div className="space-y-2">
              {visibleRows.map((item) => {
                const isWeapon = looksLikeWeapon(item);
                const mineItem = isMineItem(item);
                const quantityValue = Number(dropQuantities[item.id] || 1);
                return (
                  <div key={item.id} className="border border-slate-700 rounded-lg p-3 bg-[#060c18]">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
                          {getItemImageSrc(item) ? (
                            <img src={getItemImageSrc(item)} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[10px] text-slate-500">No tiene nada</span>
                          )}
                        </div>
                        <div>
                          <div className="text-cyan-100 font-semibold">{item.name} x{item.quantity || 1}</div>
                          <div className="text-slate-400 text-xs">
                            Tipo: {TYPE_LABELS[Number(item.item_type)] || `Tipo ${item.item_type}`}
                            {' | '}Nivel req: {item.level_required || 0}
                            {isWeapon
                              ? ` | Daño: ${item.power || 0} | Municion: ${ammoShots} disparos`
                              : Number(item.item_type) === 2
                                ? ` | Balas por cargador: ${item.power || 0}`
                                : ` | Potencia: ${item.power || 0}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isWeapon && SLOT_META.map((slot) => {
                          const alreadyInThisSlot = Number(payload?.equipped?.[slot.key]?.id || 0) === Number(item.id);
                          const alreadyEquippedInAnotherSlot = Object.values(payload?.equipped || {}).some((equippedItem) => Number(equippedItem?.id || 0) === Number(item.id));
                          const noUnitsLeft = !alreadyInThisSlot && alreadyEquippedInAnotherSlot;
                          return (
                            <button
                              key={`${item.id}-${slot.key}`}
                              onClick={() => equipItem(item.id, slot.key)}
                              disabled={busy || noUnitsLeft}
                              className="rounded-md border border-cyan-400/50 px-2 py-1 text-xs text-cyan-200 disabled:opacity-40"
                              title={noUnitsLeft ? 'Esta unidad ya esta equipada en otro slot' : `Equipar en ${slot.label}`}
                            >
                              {slot.label}
                            </button>
                          );
                        })}
                        <div className="flex flex-wrap items-center gap-2">
                          {!mineItem && (
                            <>
                              {Number(item.quantity || 1) > 1 && (
                                <input
                                  type="number"
                                  min="1"
                                  max={Number(item.quantity || 1)}
                                  value={quantityValue}
                                  onChange={(event) => {
                                    const next = Math.max(1, Math.min(Number(event.target.value || 1), Number(item.quantity || 1)));
                                    setDropQuantities((current) => ({ ...current, [item.id]: next }));
                                  }}
                                  className="w-20 rounded-md border border-slate-600 bg-[#0c1425] px-2 py-1 text-xs text-slate-100"
                                />
                              )}
                              <button
                                type="button"
                                disabled={busy || inCity || (isWeapon && Object.values(payload?.equipped || {}).some((equippedItem) => Number(equippedItem?.id || 0) === Number(item.id)))}
                                onClick={() => void dropItem(item, 'ground')}
                                className="rounded-md border border-amber-400/50 px-2 py-1 text-xs text-amber-200 disabled:opacity-40"
                                title={inCity ? `Dentro de ${cityName} usa el almacen para mover este objeto` : isWeapon && Object.values(payload?.equipped || {}).some((equippedItem) => Number(equippedItem?.id || 0) === Number(item.id))
                                  ? 'Retira el arma antes de soltarla'
                                  : 'Soltar al suelo'}
                              >
                                Soltar
                              </button>
                            </>
                          )}
                          {mineItem && (
                            <button
                              type="button"
                              disabled={busy || inCity}
                              onClick={() => void dropItem(item, 'mine')}
                              className="rounded-md border border-fuchsia-400/50 px-2 py-1 text-xs text-fuchsia-200 disabled:opacity-40"
                              title={inCity ? `Dentro de ${cityName} usa el almacen para mover este objeto` : 'Instalar una mina en una casilla adyacente libre'}
                            >
                              Instalar mina
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 border-t border-slate-700 pt-3">
              <h3 className="text-cyan-200 font-semibold text-sm mb-2">Armas detectadas en mochila: {backpackWeapons.length}</h3>
              <p className="text-slate-400 text-xs">
                Si el cargador llega a 0, se consume y no podras disparar hasta tener otra caja de municion.
              </p>
              <p className="text-slate-500 text-xs mt-2">
                Los objetos soltados se quedan en la casilla actual y, si sueltas varias unidades del mismo objeto, se apilan en la misma casilla.
                Las minas se instalan una a una en una casilla adyacente libre.
              </p>
            </div>
          </section>
        </div>
      </div>

    </div>
  );
}



